import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useParams } from 'react-router-dom';
import { manualHTML } from './manualContent';
import { manualHTML as manualHTMLBg } from './manualContent.bg';
import { PiTextAlignLeftLight, PiCaretDownLight } from 'react-icons/pi';
import Badge from '../../shared/TailwindComponents/Badge';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import { SELECTED_TINT } from '../../shared/TailwindComponents/FieldShell';
import GlassCard from '../../shared/brand/GlassCard';

interface Subsection {
  title: string;
  id: string;
}

interface Subpage {
  title: string;
  slug: string;
  processedHTML: string;
  subsections: Subsection[];
}

interface Eyebrow {
  title: string;
  slug: string;
  subpages: Subpage[];
}

// Status markers appear as inline text in the manual content; match English + Bulgarian
// so badges keep working when the localized (bg) manual is displayed.
const MANUAL_MARKERS = {
  comingSoon: ['Coming Soon', 'Скоро'],
  earlyAccess: ['Early access', 'Ранен достъп'],
  adminOnly: ['administrators only', 'само за администратори'],
};
const hasManualMarker = (html: string | undefined, keys: string[]): boolean =>
  !!html && keys.some((k) => html.includes(k));

const UserManual: React.FC = () => {
  const { t, i18n } = useTranslation();
  const params = useParams<{ eyebrowSlug?: string; subpageSlug?: string }>();
  const eyebrowSlug = params.eyebrowSlug || 'welcome';
  const subpageSlug = params.subpageSlug;
  const history = useHistory();
  const [loading, setLoading] = React.useState(true);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(new Set([eyebrowSlug]));

  const { eyebrows } = React.useMemo(() => {
    const parser = new DOMParser();
    const localizedHTML = i18n.language?.startsWith('bg') ? manualHTMLBg : manualHTML;
    const doc = parser.parseFromString(localizedHTML, 'text/html');

    const eyebrows: Eyebrow[] = [];

    const tocUL = doc.querySelector('nav ul');
    if (tocUL) {
      Array.from(tocUL.querySelectorAll('li a')).forEach((a) => {
        const title = a.textContent?.trim() || '';
        const slug = a.getAttribute('href')?.substring(1) || '';
        eyebrows.push({ title, slug, subpages: [] });
      });
    }

    Array.from(doc.querySelectorAll('main section[id]')).forEach((section) => {
      const sectionSlug = section.id;
      const eyebrow = eyebrows.find(b => b.slug === sectionSlug);
      if (!eyebrow) return;

      const h1 = section.querySelector('h1');
      if (h1) h1.remove();

      let currentSubpageHTML = '';
      let currentSubpageTitle = '';
      let currentSubpageSlug = '';
      let currentSubsections: Subsection[] = [];

      Array.from(section.children).forEach((child) => {
        if (child.tagName === 'H2') {
          if (currentSubpageTitle && currentSubpageHTML) {
            const { processedHTML } = processSubpageHTML(currentSubpageHTML, currentSubsections);
            eyebrow.subpages.push({
              title: currentSubpageTitle,
              slug: currentSubpageSlug,
              processedHTML,
              subsections: currentSubsections,
            });
          }

          currentSubpageTitle = child.textContent?.trim() || '';
          currentSubpageSlug = currentSubpageTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
          currentSubpageHTML = child.outerHTML;
          currentSubsections = [];
        } else {
          if (!currentSubpageTitle) {
            currentSubpageTitle = t("Introduction");
            currentSubpageSlug = "introduction";
            currentSubsections = [];
          }

          currentSubpageHTML += child.outerHTML;

          if (child.tagName === 'H3') {
            const title = child.textContent?.trim() || '';
            const id = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
            child.setAttribute('id', id);
            currentSubsections.push({ title, id });
          }
        }
      });

      if (currentSubpageTitle && currentSubpageHTML) {
        const { processedHTML } = processSubpageHTML(currentSubpageHTML, currentSubsections);
        eyebrow.subpages.push({
          title: currentSubpageTitle,
          slug: currentSubpageSlug,
          processedHTML,
          subsections: currentSubsections,
        });
      }
    });


    return { eyebrows };
  }, [i18n.language]);

  function processSubpageHTML(html: string, subsections: Subsection[]): { processedHTML: string } {
    return { processedHTML: html };
  }

  // Compute current page and badge status early.
  // Detection is *case-sensitive* on purpose: section authors use the title-cased
  // phrase ("Coming Soon" / "Early access") to mark a section's overall status,
  // while inline footnotes like "(coming soon)" inside an otherwise-shipped
  // section (e.g. external calendar integration) use lowercase and must NOT
  // trigger the badge.
  const currentEyebrow = eyebrows.find(b => b.slug === eyebrowSlug);
  const currentSubpage = currentEyebrow?.subpages.find(p => p.slug === subpageSlug);
  const excludeFromBadge = currentSubpage && ['introduction', 'user-manual'].includes(currentSubpage.slug);
  const isComingSoon = !excludeFromBadge && hasManualMarker(currentSubpage?.processedHTML, MANUAL_MARKERS.comingSoon);
  // "Early access" is mutually exclusive with Coming Soon - a section is one or the other.
  const isEarlyAccess = !excludeFromBadge && !isComingSoon && hasManualMarker(currentSubpage?.processedHTML, MANUAL_MARKERS.earlyAccess);

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    // Set IDs on h3 elements for anchor links
    if (mainRef.current) {
      const h3Elements = mainRef.current.querySelectorAll('h3');
      h3Elements.forEach((h3) => {
        if (!h3.id) {
          const title = h3.textContent?.trim() || '';
          const id = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
          h3.id = id;
        }
      });
      mainRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [subpageSlug, eyebrowSlug]);

  useEffect(() => {
    const currentEyebrow = eyebrows.find(b => b.slug === eyebrowSlug);
    if (!currentEyebrow || currentEyebrow.subpages.length === 0) {
      // Default landing: the Welcome chapter's intro (the welcome section has no H2,
      // so the parser auto-creates an "introduction" subpage).
      history.replace('/help/user-manual/welcome/introduction');
      return;
    }

    // Auto-expand current section
    setExpandedSections(prev => new Set([...prev, eyebrowSlug]));

    let targetSubpageSlug = subpageSlug;
    if (!targetSubpageSlug) {
      targetSubpageSlug = currentEyebrow.subpages[0].slug;
    }

    const currentSubpage = currentEyebrow.subpages.find(p => p.slug === targetSubpageSlug);
    if (!currentSubpage) {
      targetSubpageSlug = currentEyebrow.subpages[0].slug;
    }

    if (subpageSlug !== targetSubpageSlug) {
      history.replace(`/help/user-manual/${eyebrowSlug}/${targetSubpageSlug}`);
    }
  }, [eyebrowSlug, subpageSlug, eyebrows, history]);

  if (!currentEyebrow || !currentSubpage) {
    return null;
  }

  if (loading) {
    // The manual is a canvas between two docked rails - the same silhouette the
    // `detail` skeleton draws, so the swap into content doesn't jump.
    return <Skeleton variant="detail" />;
  }

  return (
    <div className="flex h-full overflow-hidden bg-bg">
      <aside className="w-64 border-e border-hairline p-4 overflow-y-auto shrink-0 transition-all duration-300 custom-scrollbar-main">
        <nav>
          <ul className="space-y-2">
            {eyebrows.map((brow) => {
              const isExpanded = expandedSections.has(brow.slug);
              return (
                <li key={brow.slug}>
                  <button
                    onClick={() => {
                      const newExpanded = new Set(expandedSections);
                      if (newExpanded.has(brow.slug)) {
                        newExpanded.delete(brow.slug);
                      } else {
                        newExpanded.add(brow.slug);
                      }
                      setExpandedSections(newExpanded);
                    }}
                    className="w-full text-start text-xs font-medium text-fg-body mb-1 p-2 rounded hover:bg-surface-sunken transition-colors duration-200 flex items-center justify-between"
                  >
                    <span>{brow.title}</span>
                    <PiCaretDownLight
                      size={16}
                      className="transition-transform duration-200"
                      style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}
                    />
                  </button>
                  {isExpanded && (
                    <ul className="space-y-1 ps-4 mt-1">
                      {brow.subpages.map((page) => {
                        const excludeFromBadge = ['introduction', 'user-manual'].includes(page.slug);
                        const pageIsComingSoon = !excludeFromBadge && hasManualMarker(page.processedHTML, MANUAL_MARKERS.comingSoon);
                        const pageIsEarlyAccess = !excludeFromBadge && !pageIsComingSoon && hasManualMarker(page.processedHTML, MANUAL_MARKERS.earlyAccess);
                        const isAdmin = hasManualMarker(page.processedHTML, MANUAL_MARKERS.adminOnly) || page.slug === 'admin-dashboard';
                        return (
                          <li key={page.slug}>
                            <Link
                              to={`/help/user-manual/${brow.slug}/${page.slug}`}
                              className={`flex items-center justify-between text-xs px-2 py-1 rounded border border-transparent transition-colors duration-200 ${
                                eyebrowSlug === brow.slug && subpageSlug === page.slug
                                  ? SELECTED_TINT
                                  : 'text-fg-body hover:bg-surface-sunken'
                              }`}
                            >
                              <span>{page.title}</span>
                              {pageIsComingSoon && (
                                <Badge color="amber" className="ms-2">{t('Coming soon')}</Badge>
                              )}
                              {pageIsEarlyAccess && (
                                <Badge color="green" className="ms-2">{t('Early')}</Badge>
                              )}
                              {isAdmin && (
                                <Badge color="blue" className="ms-2">{t('Admin')}</Badge>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      <main ref={mainRef} className="flex-1 p-8 overflow-y-auto transition-all duration-300 custom-scrollbar-main">
        <GlassCard padding="lg" className="max-w-3xl mx-auto animate-fade-in">
          <div className="mb-6 flex items-center gap-2">
            <Badge color="blue">{currentEyebrow.title}</Badge>
            {isComingSoon && <Badge color="amber">{t('Coming soon')}</Badge>}
            {isEarlyAccess && <Badge color="green">{t('Early access')}</Badge>}
          </div>
          <div
            className="prose dark:prose-invert max-w-none [&_h2]:text-base [&_h2]:font-medium [&_h2]:mt-0 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mt-10 [&_h3]:pt-8 [&_h3]:border-t [&_h3]:border-hairline-soft [&_.tip]:bg-info-50 [&_.tip]:p-4 [&_.tip]:rounded-md [&_.tip]:border [&_.tip]:border-info-200 [&_.tip]:text-info-800 dark:[&_.tip]:bg-info-900 dark:[&_.tip]:text-info-200 dark:[&_.tip]:border-info-800 [&_.alert]:bg-warning-50 [&_.alert]:p-4 [&_.alert]:rounded-md [&_.alert]:border [&_.alert]:border-warning-200 [&_.alert]:text-warning-800 dark:[&_.alert]:bg-warning-900 dark:[&_.alert]:text-warning-200 dark:[&_.alert]:border-warning-800"
            dangerouslySetInnerHTML={{ __html: currentSubpage.processedHTML }}
          />
        </GlassCard>
      </main>

      <aside className="w-64 border-s border-hairline p-4 overflow-y-auto shrink-0 transition-all duration-300">
        <div className='flex items-center gap-2 mb-4'>
            <PiTextAlignLeftLight size={16} />
        <h3 className="text-sm font-medium">{t('In this section')}</h3>
        </div>
        <nav>
          <ul className="space-y-2 ps-3">
            {currentSubpage.subsections.map((sub) => (
              <li key={sub.id}>
                <a
                  href={`#${sub.id}`}
                  className="block text-xs px-2 py-1 rounded text-fg-body hover:bg-surface-sunken transition-colors duration-200"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById(sub.id);
                    if (element && mainRef.current) {
                      const main = mainRef.current;
                      const rect = element.getBoundingClientRect();
                      const mainRect = main.getBoundingClientRect();
                      const offset = rect.top - mainRect.top + main.scrollTop - 50;
                      main.scrollTo({ top: offset, behavior: 'smooth' });
                    }
                  }}
                >
                  {sub.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </div>
  );
};

export default UserManual;
